import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../../../firebase";
import { isFinancialTenantScopedActive } from "../../../../../financialDashboard/auth/FinancialTenantContext";
import {
  canAccessSupplierFinanceOperations,
  canApproveSupplierBankCompliance,
  canApproveSupplierDuplicateOnboarding,
  canOnboardSupplier,
  canRequestSupplierCfoReview,
} from "../../../../../financialDashboard/financialRoleCapabilities";

export const SUPPLIER_ARCHIVE_ROLES = [
  "EXCO",
  "CFO",
  "cfo",
  "exco",
  "admin",
  "manager",
];

/** CFO / Manager (EXCO) may edit banking & compliance without extra review. */
export const SUPPLIER_BANK_COMPLIANCE_APPROVER_ROLES = [
  "exco",
  "cfo",
  "EXCO",
  "CFO",
  "manager",
];

const accessCache = new Map();

export const normalizeSupplierRoleField = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  return [];
};

export const hasSupplierOnboardingRole = (roleValue) =>
  canOnboardSupplier(normalizeSupplierRoleField(roleValue));

export const hasSupplierDuplicateApproverRole = (roleValue) =>
  canApproveSupplierDuplicateOnboarding(normalizeSupplierRoleField(roleValue));

export const hasSupplierCfoReviewRequesterRole = (roleValue) =>
  canRequestSupplierCfoReview(normalizeSupplierRoleField(roleValue));

export const hasSupplierFinanceRole = (roleValue) => {
  const roles = normalizeSupplierRoleField(roleValue);
  return (
    canAccessSupplierFinanceOperations(roles) || canOnboardSupplier(roles)
  );
};

export const hasSupplierArchiveRole = (roleValue) => {
  const allowed = new Set(
    SUPPLIER_ARCHIVE_ROLES.map((role) => role.toLowerCase()),
  );
  return normalizeSupplierRoleField(roleValue).some((role) =>
    allowed.has(String(role || "").trim().toLowerCase()),
  );
};

export const hasSupplierBankComplianceApproverRole = (roleValue) =>
  canApproveSupplierBankCompliance(normalizeSupplierRoleField(roleValue));

/** Non-approver editors (User, leftover admin, Viewer, Unity claim roles). */
export const isFinanceAdminOnly = (roleValue) =>
  hasSupplierCfoReviewRequesterRole(roleValue);

const buildAccessCacheKey = ({ email, company, collectionBrand }) =>
  [String(email || "").trim().toLowerCase(), company, collectionBrand].join("::");

export const fetchSupplierFinanceAccess = async ({
  email,
  company,
  collectionBrand,
  fallbackRoleValue,
} = {}) => {
  const fallbackRoles = normalizeSupplierRoleField(fallbackRoleValue);
  const fallbackAuthorized = hasSupplierFinanceRole(fallbackRoles);

  if (isFinancialTenantScopedActive() && fallbackAuthorized) {
    return {
      authorized: true,
      roles: fallbackRoles,
      source: "platform_roles",
    };
  }

  if (!email || !company || !collectionBrand) {
    return {
      authorized: fallbackAuthorized,
      roles: fallbackRoles,
      source: fallbackAuthorized ? "platform_roles" : "missing_context",
    };
  }

  const key = buildAccessCacheKey({ email, company, collectionBrand });
  const cached = accessCache.get(key);
  if (cached) {
    return cached instanceof Promise ? cached : Promise.resolve(cached);
  }

  const promise = (async () => {
    const userDocRef = doc(db, company, collectionBrand, "Users", email);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      const result = {
        authorized: fallbackAuthorized,
        roles: fallbackRoles,
        source: fallbackAuthorized ? "platform_roles" : "missing_user_doc",
      };
      accessCache.set(key, result);
      return result;
    }

    const roles = normalizeSupplierRoleField(
      userDocSnap.data()?.role ?? userDocSnap.data()?.userRole,
    );
    const mergedRoles = Array.from(new Set([...fallbackRoles, ...roles]));
    const result = {
      authorized: hasSupplierFinanceRole(mergedRoles),
      roles: mergedRoles,
      source: "users_doc",
    };
    accessCache.set(key, result);
    return result;
  })();

  accessCache.set(key, promise);

  try {
    return await promise;
  } catch (error) {
    accessCache.delete(key);
    throw error;
  }
};

export const useSupplierFinanceAccess = () => {
  const { email, company, collectionBrand, userRole } = useSelector(
    (state) => state.heroUser,
  );
  const [state, setState] = useState({
    loading: true,
    authorized: false,
    roles: [],
  });

  useEffect(() => {
    let isMounted = true;

    const loadAccess = async () => {
      if (!email || !company || !collectionBrand) {
        if (isMounted) {
          setState({
            loading: false,
            authorized: false,
            roles: [],
          });
        }
        return;
      }

      try {
        const access = await fetchSupplierFinanceAccess({
          email,
          company,
          collectionBrand,
          fallbackRoleValue: userRole,
        });
        if (!isMounted) return;
        setState({
          loading: false,
          authorized: access.authorized,
          roles: access.roles,
        });
      } catch (_error) {
        if (!isMounted) return;
        setState({
          loading: false,
          authorized: false,
          roles: [],
        });
      }
    };

    setState((prev) => ({ ...prev, loading: true }));
    void loadAccess();

    return () => {
      isMounted = false;
    };
  }, [email, company, collectionBrand, userRole]);

  return state;
};
